import { modulesAtom } from "atoms/GlobalAtoms";
import ReadableFilter from "components/CRM/ReadableFilter";
import FiltersButton from "components/SavedFilters/FiltersButton";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { SelectPicker } from "rsuite";
import _ from "underscore";

const FiltersHeader = () => {
  return (
    <>
      <h3 className="mb-0">Default Filters</h3>
      <small>
        Add data source filters that will persist across every page in this
        workspace.
      </small>
      <hr className="my-2" />
    </>
  );
};

const Filter = ({
  filter,
  module,
  i,
  handleFilterUpdate,
  handleFilterClear,
  handleDelete,
}) => {
  const [query, setQuery] = useState({});

  useEffect(() => {
    setQuery(filter.Filter);
  }, [filter.Filter]);

  return (
    <div className="d-flex align-items-center justify-content-between mt-2 border bg-ultralight rounded p-2">
      <div className="d-flex align-items-center">
        <i className="mdi mdi-database ml-2 mr-3" style={{ fontSize: 24 }} />
        <div className="font-weight-500">
          {module.Name}
          <ReadableFilter
            moduleAPIName={filter.ModuleAPIName}
            query={filter.Filter}
            fallbackMessage={
              <div className="small font-weight-400">
                No filters have been added
              </div>
            }
          />
        </div>
      </div>
      <div className="d-flex">
        <FiltersButton
          currentModule={module}
          savedQuery={filter.Filter}
          query={query}
          setQuery={setQuery}
          onApply={(query) => handleFilterUpdate(i, query)}
          onClear={() => handleFilterClear(i)}
          checkForFields={false}
          showVariables={true}
        />
        <Button
          size="sm"
          className="btn-icon-only"
          outline
          color="danger"
          onClick={() => handleDelete(i)}
        >
          <i className="mdi mdi-close" />
        </Button>
      </div>
    </div>
  );
};

export const useNonDefaultModules = () => {
  const modules = useAtomValue(modulesAtom);
  const [nonDefaultModules, setNonDefaultModules] = useState([]);
  const [modulesMap, setModulesMap] = useState({});

  useEffect(() => {
    if (!modules || !modules.length) return;

    const nonDefaultMods = [];
    const modulesMap = {};

    for (const module of modules) {
      if (!module.Default) {
        nonDefaultMods.push(module);
      }

      modulesMap[module.APIName] = module;
    }

    setNonDefaultModules(nonDefaultMods);
    setModulesMap(modulesMap);
  }, [modules]);

  return { nonDefaultModules, modulesMap };
};

const WorkspaceFiltersSection = ({ values, setValues, useHeader = true }) => {
  const { nonDefaultModules, modulesMap } = useNonDefaultModules();

  const handleSelect = (moduleAPIName) => {
    setValues((prev) => ({
      ...prev,
      Filters: [...prev.Filters, { ModuleAPIName: moduleAPIName, Filter: {} }],
    }));
  };

  const handleFilterUpdate = (index, filter) => {
    setValues((prev) => {
      const newFilters = [...prev.Filters];
      newFilters[index] = {
        ...newFilters[index],
        Filter: filter,
      };

      return { ...prev, Filters: newFilters };
    });
  };

  const handleFilterClear = (index) => {
    setValues((prev) => {
      const newFilters = [...prev.Filters];
      newFilters[index] = {
        ...newFilters[index],
        Filter: {},
      };

      return { ...prev, Filters: newFilters };
    });
  };

  const handleDelete = (deleteIndex) => {
    setValues((prev) => {
      const newFilters = [...prev.Filters];
      newFilters.splice(deleteIndex, 1);

      return { ...prev, Filters: newFilters };
    });
  };

  if (!nonDefaultModules.length) {
    return (
      <>
        {useHeader && <FiltersHeader />}
        <div className="small border bg-ultralight rounded p-2 font-weight-500">
          You haven't created any custom data sources yet.
          <br /> You can come back and add filters after creating your own Data
          Sources
        </div>
      </>
    );
  }

  return (
    <>
      {useHeader && <FiltersHeader />}
      <h4 className={useHeader ? "mt-3" : ""}>
        Select a data source you want to add filters for
      </h4>
      <SelectPicker
        value={null}
        data={nonDefaultModules.filter(
          (mod) => !_.findWhere(values?.Filters, { ModuleAPIName: mod.APIName })
        )}
        labelKey={"Name"}
        valueKey={"APIName"}
        block
        placeholder="Select a data source"
        onSelect={handleSelect}
      />
      {values?.Filters?.map((filter, i) => {
        const mod = modulesMap[filter.ModuleAPIName];

        return (
          <Filter
            key={filter.ModuleAPIName}
            filter={filter}
            module={mod}
            i={i}
            handleFilterUpdate={handleFilterUpdate}
            handleFilterClear={handleFilterClear}
            handleDelete={handleDelete}
          />
        );
      })}
      {(values?.Filters?.length === 0 || !!values?.Filters === false) && (
        <div
          className="mt-3 bg-ultralight border rounded px-4 py-2"
          style={{ fontSize: 14 }}
        >
          No Data Sources have been selected.
        </div>
      )}
    </>
  );
};

export default WorkspaceFiltersSection;
